import React, { useEffect, useState, useMemo } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { getProductsByCompany, getAllCompanies, getCompanyLogoLocal, saveCompanyLogoLocal } from '../api/productApi';
import { getAllCategories } from '../api/categoryApi';
import { useCategoryContext } from '../context/CategoryContext';
import { useCart } from '../context/CartContext';
import { showCustomToast } from './CustomToast';
import LoadingButton from './LoadingButton';
import './UserCategoryPage.css';
import { resolveImageUrl, resolveOriginalImageUrl } from '../utils/image';
import ProductImageModal from './ProductImageModal';
import ProductVideoModal from './ProductVideoModal';
import { updatePageSEO } from '../utils/seo';

type Product = {
  productId: number;
  productName: string;
  price: number;
  imageUrl?: string;
  imageUrls?: string[];
  description?: string;
  productDescription?: string;
  categoryId?: number;
  categoryName?: string;
  companyName?: string;
  CompanyName?: string;
  companyLogoUrl?: string;
  CompanyLogoUrl?: string;
  outOfStock?: boolean;
  videoUrl?: string;
  VideoUrl?: string;
  displayOrder?: number;
  DisplayOrder?: number;
};

export default function UserCompanyPage() {
  const { companySlug } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const stateCompanyName = location.state?.companyName;
  const stateCompanyLogoUrl = location.state?.companyLogoUrl;

  const { categories: contextCategories } = useCategoryContext();
  const [categories, setCategories] = useState<any[]>([]);
  const [resolvedCompanyName, setResolvedCompanyName] = useState<string>(stateCompanyName || '');
  const [resolvedCompanyLogo, setResolvedCompanyLogo] = useState<string>(stateCompanyLogoUrl || '');
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [addingToCart, setAddingToCart] = useState<{ [key: string]: boolean }>({});
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [selectedCategoryId, setSelectedCategoryId] = useState<number | 'ALL'>('ALL');
  const [sortBy, setSortBy] = useState<string>('category');
  const [imageModal, setImageModal] = useState<{ isOpen: boolean; images: string[]; index: number; productName: string }>({
    isOpen: false,
    images: [],
    index: 0,
    productName: ''
  });
  const [videoModal, setVideoModal] = useState<{ isOpen: boolean; videoUrl: string; productName: string }>({
    isOpen: false,
    videoUrl: '',
    productName: '',
  });
  const { addToCart, cart } = useCart();

  // Load categories for mapping CategoryId -> CategoryName
  useEffect(() => {
    if (contextCategories && contextCategories.length > 0) {
      setCategories(contextCategories);
    } else {
      getAllCategories()
        .then(res => {
          const data = Array.isArray(res) ? res : res?.value || [];
          setCategories(data);
        })
        .catch(() => setCategories([]));
    }
  }, [contextCategories]);

  const categoryNameMap = useMemo(() => {
    const map: Record<number, string> = {};
    categories.forEach((c: any) => {
      const id = Number(c.categoryID ?? c.categoryId);
      if (id) map[id] = c.categoryName || c.CategoryName || '';
    });
    return map;
  }, [categories]);

  const categoryOrderMap = useMemo(() => {
    const map: Record<number, number> = {};
    categories.forEach((c: any) => {
      const id = Number(c.categoryID ?? c.categoryId);
      if (id) {
        map[id] = c.displayOrder && c.displayOrder > 0 ? c.displayOrder : 999999;
      }
    });
    return map;
  }, [categories]);

  const getProductCategoryName = (p: Product): string => {
    if (p.categoryName) return p.categoryName;
    if ((p as any).CategoryName) return (p as any).CategoryName;
    const cId = Number(p.categoryId ?? (p as any).CategoryId ?? 0);
    if (cId && categoryNameMap[cId]) return categoryNameMap[cId];
    return 'General Fireworks';
  };

  const displayCompany = useMemo(() => {
    if (resolvedCompanyName) return resolvedCompanyName;
    if (!companySlug) return 'Brand';
    return decodeURIComponent(companySlug)
      .split('-')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }, [resolvedCompanyName, companySlug]);

  useEffect(() => {
    updatePageSEO({
      title: `Buy ${displayCompany} Fireworks Online Wholesale | Jay Bhavani Fatakda`,
      description: `Explore all authentic ${displayCompany} firecrackers and fatakda online at wholesale factory rates from Jay Bhavani Fatakda (જય ભવાની ફટાકડા સ્ટોર). Filter by category and order directly.`,
      canonicalPath: `/company/${companySlug || ''}`,
      keywords: `${displayCompany.toLowerCase()} crackers, ${displayCompany.toLowerCase()} fireworks, ${displayCompany.toLowerCase()} fatakda, jay bhavani fatakda, wholesale firecrackers`
    });
  }, [displayCompany, companySlug]);

  useEffect(() => {
    let isMounted = true;

    const resolveAndLoad = async () => {
      setLoading(true);
      try {
        let targetCompany = stateCompanyName;
        let targetLogo = stateCompanyLogoUrl || '';

        if (!targetCompany && companySlug) {
          const decoded = decodeURIComponent(companySlug);
          const normalize = (s: string) => s.toLowerCase().trim().replace(/\s+/g, '-');
          const allCompanies = await getAllCompanies();
          const matched = allCompanies.find(
            c => normalize(c.companyName) === normalize(decoded) || c.companyName.toLowerCase() === decoded.toLowerCase()
          );
          targetCompany = matched ? matched.companyName : decoded.replace(/-/g, ' ');
          if (matched?.companyLogoUrl) {
            targetLogo = matched.companyLogoUrl;
          }
        }

        if (!isMounted || !targetCompany) return;
        setResolvedCompanyName(targetCompany);

        const items = await getProductsByCompany(targetCompany);
        if (!isMounted) return;
        const list = Array.isArray(items) ? items : [];
        setProducts(list);

        const discoveredLogo =
          targetLogo ||
          list.map((p: any) => p.companyLogoUrl || p.CompanyLogoUrl).find(Boolean) ||
          getCompanyLogoLocal(targetCompany) ||
          '';
        if (discoveredLogo) {
          saveCompanyLogoLocal(targetCompany, discoveredLogo);
        }
        setResolvedCompanyLogo(discoveredLogo);
      } catch {
        if (!isMounted) return;
        showCustomToast('Failed to load company products', 'error', 2000);
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    resolveAndLoad();
    return () => { isMounted = false; };
  }, [companySlug, stateCompanyName, stateCompanyLogoUrl]);

  // Distinct categories available within this company's products
  const availableCategories = useMemo(() => {
    const map = new Map<number, { categoryId: number; categoryName: string; count: number }>();
    products.forEach(p => {
      const cId = Number(p.categoryId ?? (p as any).CategoryId ?? 0);
      const cName = getProductCategoryName(p);
      const existing = map.get(cId);
      if (existing) {
        existing.count += 1;
      } else {
        map.set(cId, { categoryId: cId, categoryName: cName, count: 1 });
      }
    });
    return Array.from(map.values()).sort((a, b) => {
      const orderA = categoryOrderMap[a.categoryId] ?? 999999;
      const orderB = categoryOrderMap[b.categoryId] ?? 999999;
      if (orderA !== orderB) return orderA - orderB;
      return a.categoryName.localeCompare(b.categoryName);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [products, categoryNameMap, categoryOrderMap]);

  // Filtered and sorted products
  const processedProducts = useMemo(() => {
    let list = [...products];

    if (selectedCategoryId !== 'ALL') {
      list = list.filter(p => Number(p.categoryId ?? (p as any).CategoryId ?? 0) === Number(selectedCategoryId));
    }

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      list = list.filter(p => {
        const name = (p.productName || '').toLowerCase();
        const desc = (p.productDescription || p.description || '').toLowerCase();
        const cat = getProductCategoryName(p).toLowerCase();
        return name.includes(q) || desc.includes(q) || cat.includes(q);
      });
    }

    if (sortBy === 'category') {
      list.sort((a, b) => {
        const catIdA = Number(a.categoryId ?? (a as any).CategoryId ?? 0);
        const catIdB = Number(b.categoryId ?? (b as any).CategoryId ?? 0);
        const orderCatA = categoryOrderMap[catIdA] ?? 999999;
        const orderCatB = categoryOrderMap[catIdB] ?? 999999;
        if (orderCatA !== orderCatB) return orderCatA - orderCatB;

        const catCompare = getProductCategoryName(a).localeCompare(getProductCategoryName(b));
        if (catCompare !== 0) return catCompare;

        const prodOrderA = ((a as any).displayOrder || (a as any).DisplayOrder) && ((a as any).displayOrder || (a as any).DisplayOrder) > 0 ? ((a as any).displayOrder || (a as any).DisplayOrder) : 999999;
        const prodOrderB = ((b as any).displayOrder || (b as any).DisplayOrder) && ((b as any).displayOrder || (b as any).DisplayOrder) > 0 ? ((b as any).displayOrder || (b as any).DisplayOrder) : 999999;
        if (prodOrderA !== prodOrderB) return prodOrderA - prodOrderB;

        return (a.productName || '').localeCompare(b.productName || '');
      });
    } else if (sortBy === 'price-low') {
      list.sort((a, b) => a.price - b.price);
    } else if (sortBy === 'price-high') {
      list.sort((a, b) => b.price - a.price);
    } else if (sortBy === 'name') {
      list.sort((a, b) => (a.productName || '').localeCompare(b.productName || ''));
    }

    return list;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [products, selectedCategoryId, searchQuery, sortBy, categoryNameMap, categoryOrderMap]);

  const handleAddToCart = async (product: Product) => {
    const productId = product.productId.toString();
    setAddingToCart(prev => ({ ...prev, [productId]: true }));
    try {
      const firstImage = product.imageUrls && product.imageUrls.length > 0
        ? product.imageUrls[0]
        : product.imageUrl || (product as any).productImageUrl || '';

      await addToCart({
        id: productId,
        name: product.productName,
        quantity: 1,
        price: product.price,
        imageUrl: firstImage
      });

      const currentCartCount = cart.length + 1;
      showCustomToast(`✓ Added "${product.productName}" to cart! (${currentCartCount} items)`, 'success', 2000);
    } catch {
      showCustomToast('Failed to add to cart', 'error', 2000);
    } finally {
      setAddingToCart(prev => ({ ...prev, [productId]: false }));
    }
  };

  return (
    <div className="ucp-page">
      {/* Company Header Banner */}
      <div className="ucp-banner">
        <div className="ucp-banner-content">
          <div className="ucp-nav-actions">
            <button className="ucp-back-btn" onClick={() => navigate('/category')}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z"/>
              </svg>
              <span>All Categories & Brands</span>
            </button>
            <div className="ucp-badge">🏢 BRAND COLLECTION</div>
          </div>

          <div className="ucp-company-banner-title-row">
            {resolvedCompanyLogo && (
              <div className="ucp-company-banner-logo">
                <img
                  src={resolveImageUrl(resolvedCompanyLogo)}
                  alt={displayCompany}
                  onError={e => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
                />
              </div>
            )}
            <div>
              <h1 className="ucp-title">{displayCompany} <span>Fireworks</span></h1>
              <p className="ucp-breadcrumbs">
                Home &nbsp;/&nbsp; Brands &nbsp;/&nbsp; <span>{displayCompany}</span>
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="ucp-content">
        {/* Category Quick Filter Pills */}
        {availableCategories.length > 0 && (
          <div className="ucp-category-pills-bar">
            <span className="ucp-pills-label">Filter by Category:</span>
            <div className="ucp-pills-track">
              <button
                type="button"
                className={`ucp-cat-filter-pill ${selectedCategoryId === 'ALL' ? 'active' : ''}`}
                onClick={() => setSelectedCategoryId('ALL')}
              >
                <span>✨ All Categories</span>
                <span className="ucp-pill-count">{products.length}</span>
              </button>
              {availableCategories.map(cat => (
                <button
                  key={cat.categoryId}
                  type="button"
                  className={`ucp-cat-filter-pill ${selectedCategoryId === cat.categoryId ? 'active' : ''}`}
                  onClick={() => setSelectedCategoryId(cat.categoryId)}
                >
                  <span>🧨 {cat.categoryName}</span>
                  <span className="ucp-pill-count">{cat.count}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Controls Bar: Search, Category Select & Sort */}
        <div className="ucp-controls-bar">
          <div className="ucp-search-wrap">
            <span className="ucp-search-icon">🔍</span>
            <input
              type="text"
              placeholder={`Search products or category in ${displayCompany}...`}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="ucp-search-input"
            />
            {searchQuery && (
              <button className="ucp-clear-search" onClick={() => setSearchQuery('')}>✕</button>
            )}
          </div>

          <div className="ucp-filters-group">
            <div className="ucp-sort-wrap">
              <label htmlFor="company-cat-filter" className="ucp-sort-label">Category:</label>
              <select
                id="company-cat-filter"
                value={selectedCategoryId}
                onChange={(e) => setSelectedCategoryId(e.target.value === 'ALL' ? 'ALL' : Number(e.target.value))}
                className="ucp-sort-select"
              >
                <option value="ALL">All Categories ({products.length})</option>
                {availableCategories.map(cat => (
                  <option key={cat.categoryId} value={cat.categoryId}>
                    {cat.categoryName} ({cat.count})
                  </option>
                ))}
              </select>
            </div>

            <div className="ucp-sort-wrap">
              <label htmlFor="company-product-sort" className="ucp-sort-label">Sort by:</label>
              <select
                id="company-product-sort"
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="ucp-sort-select"
              >
                <option value="category">Category Wise (A-Z)</option>
                <option value="featured">Featured Deals</option>
                <option value="price-low">Price: Low to High</option>
                <option value="price-high">Price: High to Low</option>
                <option value="name">Product Name (A-Z)</option>
              </select>
            </div>
          </div>
        </div>

        {/* Product Count Strip */}
        <div className="ucp-count-strip">
          <span>
            Showing <strong>{processedProducts.length}</strong> items from <strong>{displayCompany}</strong>
            {selectedCategoryId !== 'ALL' && availableCategories.find(c => c.categoryId === selectedCategoryId)
              ? ` in ${availableCategories.find(c => c.categoryId === selectedCategoryId)?.categoryName}`
              : ''}
          </span>
          <span className="ucp-discount-pill">✨ Up to 50% Off Factory Rates</span>
        </div>

        {/* Products Grid */}
        <div className="ucp-product-grid">
          {loading ? (
            <div className="ucp-loading">
              <div className="ucp-spinner"></div>
              <p>Loading {displayCompany} products...</p>
            </div>
          ) : processedProducts.length > 0 ? (
            processedProducts.map(product => {
              const isAdded = cart.some(item => item.id === product.productId.toString());
              const productId = product.productId.toString();
              const mrpPrice = product.price > 0 ? product.price * 2 : 0;

              const rawImages = product.imageUrls && product.imageUrls.length > 0
                ? product.imageUrls
                : product.imageUrl ? [product.imageUrl] : [];
              const allImages = rawImages.map((r: string) => resolveImageUrl(r)).filter(Boolean) as string[];
              const resolved = allImages.length > 0 ? allImages[0] : null;

              const targetVideoUrl = product.videoUrl || product.VideoUrl;
              const prodCategoryName = getProductCategoryName(product);
              const prodCompany = product.companyName || product.CompanyName || displayCompany;
              const prodCompanyLogo = product.companyLogoUrl || product.CompanyLogoUrl || resolvedCompanyLogo;
              const prodDesc = product.productDescription || product.description;

              return (
                <div className="ucp-card" key={product.productId}>
                  <div className="ucp-card-media-wrap">
                    {product.outOfStock ? (
                      <span className="ucp-stock-badge out">OUT OF STOCK</span>
                    ) : (
                      <span className="ucp-stock-badge in">50% OFF</span>
                    )}

                    <div
                      className="ucp-image-box"
                      onClick={() => setImageModal({ isOpen: true, images: allImages, index: 0, productName: product.productName })}
                    >
                      {resolved ? (
                        <img
                          src={resolved}
                          alt={product.productName}
                          className="ucp-product-image"
                          loading="lazy"
                          decoding="async"
                          onError={(e) => {
                            const t = e.currentTarget as HTMLImageElement;
                            const firstRaw = rawImages && rawImages.length > 0 ? rawImages[0] : null;
                            const fallbackUrl = firstRaw ? resolveOriginalImageUrl(firstRaw) : '';
                            if (fallbackUrl && t.src !== fallbackUrl) {
                              t.src = fallbackUrl;
                            } else {
                              t.src = 'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="160" height="140" viewBox="0 0 160 140"><rect width="160" height="140" fill="%23f8fafc"/><text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle" font-size="36">🧨</text></svg>';
                            }
                          }}
                        />
                      ) : (
                        <div className="ucp-placeholder">
                          <span>🧨</span>
                        </div>
                      )}
                      <span className="ucp-zoom-hint">🔍 Tap to Zoom</span>
                    </div>
                  </div>

                  <div className="ucp-card-body">
                    <div className="ucp-card-badges-row">
                      {prodCompany && (
                        <span className="ucp-company-tag" title={`Brand: ${prodCompany}`}>
                          {prodCompanyLogo ? (
                            <img
                              src={resolveImageUrl(prodCompanyLogo)}
                              alt={prodCompany}
                              className="ucp-company-tag-logo"
                              onError={e => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
                            />
                          ) : (
                            <span>🏢</span>
                          )}
                          <span>{prodCompany}</span>
                        </span>
                      )}
                      {prodCategoryName && (
                        <span className="ucp-category-tag" title={`Category: ${prodCategoryName}`}>
                          🧨 {prodCategoryName}
                        </span>
                      )}
                    </div>

                    <h3 className="ucp-product-name" title={product.productName}>
                      {product.productName}
                    </h3>

                    {prodDesc && (
                      <p className="ucp-product-desc">{prodDesc}</p>
                    )}

                    <div className="ucp-price-row">
                      {mrpPrice > 0 && (
                        <span className="ucp-mrp">₹{mrpPrice.toFixed(2)}</span>
                      )}
                      <span className="ucp-current-price">₹{product.price.toFixed(2)}</span>
                      <span className="ucp-save-tag">Save ₹{(mrpPrice - product.price).toFixed(0)}</span>
                    </div>

                    {targetVideoUrl && (
                      <button
                        type="button"
                        className="ucp-card-video-action"
                        onClick={() => setVideoModal({
                          isOpen: true,
                          videoUrl: targetVideoUrl,
                          productName: product.productName,
                        })}
                        title="Watch live cracker demonstration video"
                      >
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                        </svg>
                        <span>Watch Demonstration</span>
                      </button>
                    )}

                    <LoadingButton
                      onClick={() => handleAddToCart(product)}
                      loading={addingToCart[productId] || false}
                      disabled={product.outOfStock || isAdded}
                      className={`ucp-add-btn ${isAdded ? 'added' : ''}`}
                    >
                      {product.outOfStock ? 'Out of Stock' : isAdded ? '✓ Added to Cart' : '🛒 Add to Cart'}
                    </LoadingButton>
                  </div>
                </div>
              );
            })
          ) : (
            <div className="ucp-empty">
              <div className="ucp-empty-icon">📦</div>
              <h3>No Products Found</h3>
              <p>
                {searchQuery || selectedCategoryId !== 'ALL'
                  ? 'No products matched your current filter.'
                  : `No products currently listed under ${displayCompany}.`}
              </p>
              {(searchQuery || selectedCategoryId !== 'ALL') ? (
                <button
                  className="ucp-back-cta"
                  onClick={() => {
                    setSearchQuery('');
                    setSelectedCategoryId('ALL');
                  }}
                >
                  Reset Filters
                </button>
              ) : (
                <button className="ucp-back-cta" onClick={() => navigate('/category')}>
                  Browse All Categories
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      <ProductImageModal
        isOpen={imageModal.isOpen}
        onClose={() => setImageModal({ isOpen: false, images: [], index: 0, productName: '' })}
        images={imageModal.images}
        startIndex={imageModal.index}
        productName={imageModal.productName}
      />
      <ProductVideoModal
        isOpen={videoModal.isOpen}
        onClose={() => setVideoModal({ isOpen: false, videoUrl: '', productName: '' })}
        videoUrl={videoModal.videoUrl}
        productName={videoModal.productName}
      />
    </div>
  );
}
